export type OperationalRiskLevel = 'OK' | 'ALERTA' | 'CRITICO';

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

export function formatDurationClockFromMinutes(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'N/D';
  }

  const sign = value < 0 ? '-' : '';
  const totalSeconds = Math.max(Math.round(Math.abs(value) * 60), 0);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** Smart SLA label: "ATRASADO HA Xh" when negative, "Xh Xmin" when positive, "SEM SLA" when null */
export function formatSlaSmartLabel(remainingMinutes: number | null): { label: string; status: 'violated' | 'warning' | 'ok' | 'none' } {
  if (remainingMinutes === null) {
    return { label: 'SEM SLA', status: 'none' };
  }
  if (remainingMinutes < 0) {
    const absMin = Math.abs(remainingMinutes);
    const h = Math.floor(absMin / 60);
    const m = Math.round(absMin % 60);
    return { label: `ATRASADO HA ${h > 0 ? `${h}h` : ''}${m > 0 ? ` ${m}min` : ''}`.trim(), status: 'violated' };
  }
  const h = Math.floor(remainingMinutes / 60);
  const m = Math.round(remainingMinutes % 60);
  return { label: `${h > 0 ? `${h}h` : ''}${m > 0 ? ` ${m}min` : h > 0 ? '' : '< 1min'}`.trim(), status: remainingMinutes <= 60 ? 'warning' : 'ok' };
}

export function getRemainingMinutesFromDeadline(deadline?: string | null): number | null {
  if (!deadline) {
    return null;
  }

  const remainingMs = new Date(deadline).getTime() - Date.now();
  if (!Number.isFinite(remainingMs)) {
    return null;
  }

  return remainingMs / 60000;
}

export function getExecutionTotalSlaMinutes(item: {
  metadata?: Record<string, unknown> | null;
}): number | null {
  return (
    toFiniteNumber(item.metadata?.expectedDurationMinutes) ??
    toFiniteNumber(item.metadata?.expected_duration_minutes) ??
    toFiniteNumber(item.metadata?.slaTotalMinutes) ??
    toFiniteNumber(item.metadata?.sla_total_minutes)
  );
}

export function getExecutionRemainingSlaMinutes(item: {
  metadata?: Record<string, unknown> | null;
  elapsedMinutes?: number | null;
  overdueMinutes?: number | null;
}): number | null {
  if (typeof item.overdueMinutes === 'number' && Number.isFinite(item.overdueMinutes) && item.overdueMinutes > 0) {
    return -item.overdueMinutes;
  }

  const totalMinutes = getExecutionTotalSlaMinutes(item);
  if (totalMinutes !== null && typeof item.elapsedMinutes === 'number' && Number.isFinite(item.elapsedMinutes)) {
    return totalMinutes - item.elapsedMinutes;
  }

  return totalMinutes;
}

export function getTaskTotalSlaMinutes(task: {
  createdAt?: string | null;
  slaDeadline?: string | null;
  metadata?: Record<string, unknown> | null;
}): number | null {
  if (task.createdAt && task.slaDeadline) {
    const totalMs = new Date(task.slaDeadline).getTime() - new Date(task.createdAt).getTime();
    if (Number.isFinite(totalMs) && totalMs > 0) {
      return totalMs / 60000;
    }
  }

  return (
    toFiniteNumber(task.metadata?.expectedDurationMinutes) ??
    toFiniteNumber(task.metadata?.expected_duration_minutes) ??
    toFiniteNumber(task.metadata?.slaTotalMinutes) ??
    toFiniteNumber(task.metadata?.sla_total_minutes)
  );
}

export function getOperationalRiskLevel(remainingMinutes: number | null, totalMinutes: number | null): OperationalRiskLevel {
  if (remainingMinutes !== null && remainingMinutes < 0) {
    return 'CRITICO';
  }

  if (remainingMinutes !== null && totalMinutes !== null && totalMinutes > 0 && remainingMinutes <= totalMinutes * 0.25) {
    return 'ALERTA';
  }

  return 'OK';
}

export function getOperationalRiskRank(level: OperationalRiskLevel): number {
  switch (level) {
    case 'CRITICO':
      return 3;
    case 'ALERTA':
      return 2;
    case 'OK':
    default:
      return 1;
  }
}

export function getOperationalRiskPresentation(level: OperationalRiskLevel): {
  icon: string;
  label: OperationalRiskLevel;
  className: string;
} {
  switch (level) {
    case 'CRITICO':
      return {
        icon: '🔴',
        label: 'CRITICO',
        className: 'text-rose-700 dark:text-rose-300',
      };
    case 'ALERTA':
      return {
        icon: '🟡',
        label: 'ALERTA',
        className: 'text-amber-700 dark:text-amber-300',
      };
    case 'OK':
    default:
      return {
        icon: '🟢',
        label: 'OK',
        className: 'text-emerald-700 dark:text-emerald-300',
      };
  }
}
